import React, { useState, useEffect, useMemo } from 'react';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Copyright from '../internals/components/Copyright';
import ChartErrorBoundary from './ChartErrorBoundary';
import {
  selectQueryDate
} from '../../features/uidata/uidataSlice';
import { useSelector, useDispatch } from 'react-redux';

import {
  selectUserrequestData, selectUserrequestStatus, fetchUserrequestDataWithFilters
} from '../../features/userrequest/userrequestSlice';



import {
  getMinTime,
  getMaxTime
} from '../utils/datetimeUtils';
import CarCharging from './CarCharging';

import { 
  OptionUsageDonut, 
  TargetVsCurrentScatter, 
  UserSelectedTimeHistogram,
  TimeHeatmap,
  EfficiencyGauge,
  ProgressTimeline
} from './charts';
import TopKPIs from './TopKPIs';
import DetailedStats from './DetailedStats';



function getDayDifference(date1, date2) {
  // Convert both dates to milliseconds
  const time1 = new Date(date1).getTime();
  const time2 = new Date(date2).getTime();
  // Calculate the difference in milliseconds
  const diffInMilliseconds = Math.abs(time1 - time2);
  // Convert milliseconds to days
  const diffInDays = diffInMilliseconds / (1000 * 60 * 60 * 24);
  return Math.floor(diffInDays);
}

function generateMongoFilter(field, startDate, endDate) {
  return {
    [field]: {
      $gte: startDate,
      $lte: endDate
    }
  };
}

export default function Analytics() {  
  const dispatch = useDispatch();
  const userrequestAPIData = useSelector(selectUserrequestData);
  const userrequestStatus = useSelector(selectUserrequestStatus);
  //
  const { beginingDate: tempBeginingDate, endDate: tempEndDate } = useSelector(selectQueryDate);
  const beginingDate = new Date(tempBeginingDate);
  const endDate = new Date(tempEndDate);
  const lastDays = getDayDifference(beginingDate, endDate);

const apiAvailable = (status) => ['idle', 'succeeded'].includes(status);

  useEffect(() => {
    if (apiAvailable(userrequestStatus)) {
      const filterEndTime = getMinTime(endDate, new Date());
      const filter = generateMongoFilter('timestamp', beginingDate, filterEndTime);
      dispatch(fetchUserrequestDataWithFilters({ filter }));
    }
  }, [tempBeginingDate, tempEndDate]);

const apiFetchSuccess = (status) => ['succeeded'].includes(status)

  // defensive data processing
  const rawResults = (userrequestAPIData && userrequestAPIData.results) || [];
  
  // Debug API data
  console.log('API Status:', userrequestStatus);
  console.log('API Data available:', !!userrequestAPIData);
  console.log('Raw results length:', rawResults?.length);
  
  // Log raw data for debugging
  console.log('Raw results:', rawResults);

  // ensure objects only and validate timestamps with more lenient checks
  const analyticsData = rawResults.filter(r => {
    // Basic data validation - just make sure it's an object
    if (!r || typeof r !== 'object') return false;
    
    // Timestamp validation - more lenient
    if (r.timestamp) {
      try {
        const date = new Date(r.timestamp);
        if (isNaN(date.getTime())) return false;
      } catch (e) {
        return false;
      }
    }
    
    // Accept the data if it has at least one of these fields
    const hasValue = r.value !== undefined;
    const hasTargetSoc = r.targetSoc !== undefined;
    const hasCurrentSoc = r.currentSoc !== undefined;
    
    return hasValue || hasTargetSoc || hasCurrentSoc;
  });

  // Ensure we have data before rendering
  if (userrequestStatus === 'loading') {
    return (
      <Box sx={{ width: '100%', textAlign: 'center', py: 4 }}>
        <Typography>Loading analytics data...</Typography>
      </Box>
    );
  }

  if (userrequestStatus === 'failed') {
    return (
      <Box sx={{ width: '100%', textAlign: 'center', py: 4 }}>
        <Typography color="error">Failed to load analytics data. Please try again later.</Typography>
      </Box>
    );
  }

  if (!analyticsData.length) {
    return (
      <Box sx={{ width: '100%', textAlign: 'center', py: 4 }}>
        <Typography>No data available for the selected time period.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      {/* cards */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography component="h2" variant="h6">
            Analytics Dashboard
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            {new Date(beginingDate).toLocaleDateString()} - {new Date(endDate).toLocaleDateString()}
          </Typography>
        </Box>

        <Grid
          container
          spacing={2}
          columns={12}
          sx={{ mb: (theme) => theme.spacing(2) }}
        >
        <Grid size={{ xs: 12, lg: 12 }}>
          <CarCharging
            beginingDate={beginingDate}
            endDate={endDate} />
        </Grid>
        
        <Grid size={{ xs: 12 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Key Performance Metrics
              <Typography component="span" variant="subtitle2" color="textSecondary" sx={{ ml: 1 }}>
                (Compared to Previous Period)
              </Typography>
            </Typography>
            <ChartErrorBoundary>
              <DetailedStats 
                data={analyticsData?.filter(d => d && typeof d === 'object' && !Array.isArray(d)) || []} 
              />
            </ChartErrorBoundary>
          </Box>
        </Grid>
        
        <Grid size={{ xs: 12 }}>
          <ChartErrorBoundary>
            <TopKPIs data={analyticsData} />
          </ChartErrorBoundary>
        </Grid>        
        <Grid size={{ xs: 12, md: 4 }}>
          <ChartErrorBoundary>
            <OptionUsageDonut data={analyticsData} />
          </ChartErrorBoundary>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <ChartErrorBoundary>
            <TargetVsCurrentScatter 
              data={analyticsData?.filter(d => 
                d && 
                (d.targetSoc !== undefined || d.target_soc !== undefined) && 
                (d.currentSoc !== undefined || d.current_soc !== undefined)
              ) || []} 
            />
          </ChartErrorBoundary>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <ChartErrorBoundary>
            <UserSelectedTimeHistogram data={analyticsData} />
          </ChartErrorBoundary>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ChartErrorBoundary>
            <TimeHeatmap data={analyticsData} />
          </ChartErrorBoundary>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartErrorBoundary>
            <EfficiencyGauge data={analyticsData} />
          </ChartErrorBoundary>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <ChartErrorBoundary>
            <ProgressTimeline data={analyticsData} />
          </ChartErrorBoundary>
        </Grid>
      </Grid>
      <Grid container spacing={2} columns={12}>
        {/* <Grid size={{ xs: 12, lg: 9 }}>
          <DispatchSummary />
        </Grid> */}
      </Grid>
      <Copyright sx={{ my: 4 }} />
    </Box>
  );
}
